import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';
import IPRestrictionsCore from '../components/IPRestrictionsCore';

interface IpEntry {
  id: number;
  ip_cidr: string;
  label: string | null;
  created_at: string;
}

interface IpRestrictionsData {
  enabled: boolean;
  entries: IpEntry[];
  your_ip: string;
}

const IPRestrictionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspaceId = parseInt(id || '0', 10);
  const { showSuccess, showError } = useToast();

  const [workspace, setWorkspace] = useState<any>(null);
  const [data, setData] = useState<IpRestrictionsData>({ enabled: false, entries: [], your_ip: '' });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [wsResponse, ipResponse] = await Promise.all([
        apiService.get(`/workspaces/${workspaceId}`),
        apiService.get(`/workspaces/${workspaceId}/ip-restrictions`),
      ]);
      setWorkspace(wsResponse.data);
      setData(ipResponse.data);
    } catch (err: any) {
      showError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const ipResponse = await apiService.get(`/workspaces/${workspaceId}/ip-restrictions`);
      setData(ipResponse.data);
    } finally {
      setRefreshing(false);
    }
  };

  const handleSetEnabled = async (enabled: boolean) => {
    await apiService.put(`/workspaces/${workspaceId}/ip-restrictions/settings`, { enabled });
    const ipResponse = await apiService.get(`/workspaces/${workspaceId}/ip-restrictions`);
    setData(ipResponse.data);
  };

  const handleAddEntry = async (ipCidr: string, label: string | null) => {
    await apiService.post(`/workspaces/${workspaceId}/ip-restrictions/entries`, { ip_cidr: ipCidr, label });
    const ipResponse = await apiService.get(`/workspaces/${workspaceId}/ip-restrictions`);
    setData(ipResponse.data);
  };

  const handleUpdateEntry = async (entryId: number, label: string | null) => {
    await apiService.put(`/workspaces/${workspaceId}/ip-restrictions/entries/${entryId}`, { label });
    const ipResponse = await apiService.get(`/workspaces/${workspaceId}/ip-restrictions`);
    setData(ipResponse.data);
  };

const handleDeleteEntry = async (entryId: number) => {
    try {
      await apiService.delete(`/workspaces/${workspaceId}/ip-restrictions/entries/${entryId}`);
      const ipResponse = await apiService.get(`/workspaces/${workspaceId}/ip-restrictions`);
      setData(ipResponse.data);
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to remove IP entry';
      throw new Error(message);
    }
  };

  return (
    <div
      style={{
        padding: '32px',
        backgroundColor: 'var(--v2-bg)',
        minHeight: '100vh',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* Breadcrumbs */}
      <nav
        style={{
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
        }}
      >
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: '0.875rem',
            color: 'var(--v2-text-tertiary)',
            fontWeight: 500,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--v2-primary)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
        >
          Workspaces
        </button>
	<ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text)', fontWeight: 500 }}>{workspace?.name}</span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>IP Restrictions</span>
      </nav>

      <IPRestrictionsCore
        enabled={data.enabled}
        entries={data.entries}
        yourIp={data.your_ip}
        loading={loading}
        error={null}
        refreshing={refreshing}
        mode="modal"
        onSetEnabled={handleSetEnabled}
        onAddEntry={handleAddEntry}
        onUpdateEntry={handleUpdateEntry}
        onDeleteEntry={handleDeleteEntry}
        onRefresh={handleRefresh}
        onSuccess={showSuccess}
        onError={showError}
      />
    </div>
  );
};

export default IPRestrictionsPage;
